/**
 * CompareSessionsView.jsx — Rendu de la comparaison de sessions déjà chargées.
 *
 * Extrait de CompareSessionsPage (D257) pour servir aussi la page publique du
 * lien de comparaison : ce composant ne charge rien, il reçoit `loaded`
 * ([{ session, readings, pagination? }]). Les courbes des mesures de
 * compareMetrics.js sont alignées « depuis le début » ou en « heure réelle »
 * (CompareChart, compareAxis.js) ; un onglet par mesure visible, plus l'onglet
 * « Tous les paramètres » (CompareAllCharts). Tableau comparatif dans
 * CompareStatsTable (P95 partagé, D200), export PDF (ComparePdfButton) dont la
 * variante 'owner' | 'vet' est transmise par le point d'entrée, jamais déduite.
 *
 * Plage d'analyse (10.5.297, useAnalysisRange) : mode d'axe et plage viennent
 * du point d'entrée (`range`, `onRangeChange`) ; la plage pilote courbes
 * (cadrage), tableau, coches et PDF. Sans choix explicite : heure réelle et
 * plage commune si les sessions se chevauchent, sinon durée commune depuis le
 * début. `rangeLocked` masque la barre de plage (lien de comparaison : le
 * serveur ne renvoie que les mesures de la période du lien).
 *
 * Emplacements du point d'entrée : `onBack` (bouton retour), `renderActions`
 * ({ analysis, alignMode, field } → boutons de l'en-tête), `notice` (ligne sous
 * le sous-titre), `pdfMention` (mention de pied de page du PDF, lien de comparaison).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALIGN_MODES } from '../charts/compareAxis';
import CompareStatsTable from './CompareStatsTable';
import ComparePdfButton from './ComparePdfButton';
import AnalysisRangeBar from './AnalysisRangeBar';
import CompareRangeWarnings from './CompareRangeWarnings';
import CompareAllCharts, { MetricChartCard } from './CompareAllCharts';
import useComparePdfExport from './useComparePdfExport';
import useAnalysisRange from './useAnalysisRange';
import { RANGES } from './analysisRange';
import { periodLine, qualityWarnings, tableNotes } from './analysisRangeText';
import { SessionLegend, DurationSummary, BackButton, compareTheme } from './_compareBlocks';
import { getComparePalette } from '../constants/comparePalette';
import { ALL_FIELD, DEFAULT_COMPARE_FIELD, findMetric, visibleMetrics } from './compareMetrics';

// Vue du graphique cadrée sur la plage, en unités de l'axe (ms ou minutes) ;
// aucune pour « Tout » (vue complète, rien de grisé).
function chartWindow(resolved) {
  if (resolved.range === RANGES.ALL) return null;
  if (resolved.align === ALIGN_MODES.CLOCK) {
    return resolved.clock ? { min: resolved.clock.start, max: resolved.clock.end } : null;
  }
  return Number.isFinite(resolved.elapsed) ? { min: 0, max: Math.ceil(resolved.elapsed / 60000) } : null;
}

const CompareSessionsView = ({ loaded, darkMode = false, variant = 'owner', range = null, onRangeChange,
  onBack, renderActions, notice = null, pdfMention, rangeLocked = false }) => {
  const { t: tr } = useTranslation();
  const [activeChart, setActiveChart] = useState(DEFAULT_COMPARE_FIELD);
  const [highlight, setHighlight] = useState(null); // index session survolée
  const analysis = useAnalysisRange(loaded, range, onRangeChange);
  const { resolved } = analysis;
  const alignMode = resolved.align;
  const pdfExport = useComparePdfExport({ loaded, alignMode, variant, analysis, mention: pdfMention,
    ready: loaded.length > 0 });
  const palette = getComparePalette(darkMode);
  const { t, c, card } = compareTheme(darkMode);

  // Sélecteur : le CO₂ n'y figure que si une session comparée en a.
  const metrics = visibleMetrics(loaded);
  const showAll = activeChart === ALL_FIELD;
  // Onglet d'une mesure disparue (CO₂ d'une autre sélection) : PM2.5.
  const activeMetric = showAll ? null : (metrics.find(m => m.field === activeChart) || findMetric(DEFAULT_COMPARE_FIELD));
  // Légende du tableau : mention du CO₂ seulement quand ses lignes sont affichées.
  const tableHasCo2 = visibleMetrics(analysis.filtered).some(m => m.field === 'co2');
  const viewWindow = chartWindow(resolved);

  const toggleStyle = (active) => ({
    padding: '7px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
    background: active ? t.accent : (darkMode ? '#282845' : '#F3F4F6'),
    color: active ? '#fff' : c.textSec,
  });

  const notes = tableNotes(analysis, tr);
  const tabs = [...metrics.map(m => ({ field: m.field, labelKey: m.labelKey })),
    { field: ALL_FIELD, labelKey: 'sessions.compare.metric.all' }];

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 0 40px' }}>

      {/* Header */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '20px', fontWeight: '700',
            color: c.accent, margin: '0 0 4px' }}>
            {tr('sessions.compare.title')}
          </h2>
          <p style={{ fontSize: '13px', color: c.textSec, margin: 0 }}>
            {tr('sessions.compare.subtitle', { count: loaded.length })}
          </p>
          {notice && <p style={{ fontSize: '12px', color: c.textSec, margin: '4px 0 0' }}>{notice}</p>}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {renderActions?.({ analysis, alignMode, field: activeChart })}
          <ComparePdfButton onClick={pdfExport.exportPdf} generating={pdfExport.generating}
            disabled={pdfExport.disabled} error={pdfExport.error} darkMode={darkMode} />
          {onBack && <BackButton onBack={onBack} c={c} />}
        </div>
      </div>

      <SessionLegend loaded={loaded} palette={palette} highlight={highlight} setHighlight={setHighlight}
        card={card} c={c} t={t} darkMode={darkMode} field={showAll ? ALL_FIELD : activeMetric.field} />

      {/* Sélecteurs : graphique (gauche), alignement des courbes (droite) */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px',
        justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {tabs.map(tab => {
            const active = showAll ? tab.field === ALL_FIELD : tab.field === activeMetric.field;
            return (
              <button key={tab.field} onClick={() => setActiveChart(tab.field)} aria-pressed={active}
                style={toggleStyle(active)}>
                {tr(tab.labelKey)}
              </button>
            );
          })}
        </div>
        <div role="group" aria-label={tr('sessions.compare.align.label')}
          style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[ALIGN_MODES.FROM_START, ALIGN_MODES.CLOCK].map(mode => (
            <button key={mode} onClick={() => analysis.setAlign(mode)} aria-pressed={alignMode === mode}
              style={toggleStyle(alignMode === mode)}>
              {tr(`sessions.compare.align.${mode}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Plage d'analyse : pilote graphique, tableau, coches et PDF */}
      {!rangeLocked && (
        <div style={card}>
          <AnalysisRangeBar resolved={resolved} onShortcut={analysis.setShortcut} onCustom={analysis.setCustom}
            buttonStyle={toggleStyle} colors={c} />
        </div>
      )}
      <CompareRangeWarnings lines={qualityWarnings(analysis, loaded, tr)} darkMode={darkMode} />

      {/* Graphiques comparatifs : une mesure, ou toutes empilées */}
      {showAll ? (
        <CompareAllCharts loaded={loaded} metrics={metrics} alignMode={alignMode} darkMode={darkMode}
          analysisWindow={viewWindow} onAnalyzeZoom={rangeLocked ? undefined : analysis.setCustom} card={card} c={c} />
      ) : (
        <MetricChartCard loaded={loaded} metric={activeMetric} alignMode={alignMode} darkMode={darkMode}
          analysisWindow={viewWindow} onAnalyzeZoom={rangeLocked ? undefined : analysis.setCustom} card={card} c={c} />
      )}

      {/* Tableau comparatif des stats, sur la plage d'analyse */}
      <div style={card}>
        <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '15px', fontWeight: '700',
          color: c.text, margin: '0 0 8px' }}>
          {tr('sessions.compare.statsTitle')}
        </h3>
        <p data-testid="compare-period" style={{ fontSize: '12px', color: c.textSec, margin: '0 0 12px' }}>
          {periodLine(resolved, tr)}
        </p>
        <div style={{ overflowX: 'auto' }}>
          <CompareStatsTable loaded={analysis.filtered} quality={analysis.sessions}
            hideBestFields={analysis.hideBestFields} colors={c} darkMode={darkMode} />
        </div>
        <p style={{ fontSize: '11px', color: c.textSec, margin: '10px 0 0' }}>
          {tr(tableHasCo2 ? 'sessions.compare.legendCo2' : 'sessions.compare.legend')}
        </p>
        {notes.map(note => (
          <p key={note} data-testid="compare-table-note" style={{ fontSize: '11px', color: c.textSec, margin: '4px 0 0' }}>
            {note}
          </p>
        ))}
      </div>

      <DurationSummary loaded={loaded} quality={analysis.sessions} palette={palette} card={card} c={c} t={t} />
    </div>
  );
};

export default CompareSessionsView;
